import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const Terms = () => {
  return (
    <>
      <SEOHead 
        title="Terms of Service | uplino.in"
        description="Read the terms of service for using uplino.in classified ads platform. Understand your rights, responsibilities, and our policies."
        keywords={["terms of service", "user agreement", "uplino terms", "legal"]}
        canonical="https://uplino.in/terms"
      />
      <div className="min-h-screen bg-slate-950">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8">
            Terms of Service
          </h1>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-400 mb-6">
              Last Updated: December 2025
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 leading-relaxed">
                  By accessing and using uplino.in ("the Platform"), you agree to be bound by these 
                  Terms of Service. If you do not agree to these terms, please do not use our services.
                  These terms apply to all users, including advertisers, visitors, and agents.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">2. Eligibility</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-3">
                <p className="text-gray-300">To use our Platform, you must:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Be at least 18 years of age</li>
                  <li>Have the legal capacity to enter into binding contracts</li>
                  <li>Not be prohibited from using our services under applicable laws</li>
                  <li>Provide accurate and complete registration information</li>
                  <li>Comply with all applicable local, state, and national laws</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">3. User Accounts</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">3.1 Account Registration</h3>
                  <p className="text-gray-300">
                    You must create an account to access certain features. You are responsible for 
                    maintaining the confidentiality of your account credentials and for all activities 
                    under your account.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">3.2 Account Types</h3>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li><span className="text-rose-400">Advertiser:</span> Can post and manage advertisements</li>
                    <li><span className="text-amber-400">Agent:</span> Authorized to process wallet top-ups</li>
                    <li><span className="text-blue-400">Admin:</span> Platform management and moderation</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">3.3 Account Security</h3>
                  <p className="text-gray-300">
                    You must immediately notify us of any unauthorized use of your account. We are not 
                    liable for any loss arising from unauthorized access to your account.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">4. Advertisements</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">4.1 Content Requirements</h3>
                  <p className="text-gray-300 mb-2">All advertisements must:</p>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Be accurate, truthful, and not misleading</li>
                    <li>Not contain illegal, offensive, or prohibited content</li>
                    <li>Not infringe on any third-party rights</li>
                    <li>Comply with our content guidelines</li>
                    <li>Not promote illegal activities</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">4.2 Prohibited Content</h3>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Content involving minors (under 18)</li>
                    <li>Non-consensual activities</li>
                    <li>Human trafficking or exploitation</li>
                    <li>Illegal drugs or substances</li>
                    <li>Hate speech or discrimination</li>
                    <li>Fraudulent or scam content</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">4.3 Ad Duration & Expiry</h3>
                  <p className="text-gray-300">
                    All advertisements are valid for 3 days from the date of posting. After expiry, 
                    ads will automatically be removed from public listings. Users can renew ads by 
                    making a new payment.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">5. Payments & Wallet</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">5.1 Wallet System</h3>
                  <p className="text-gray-300">
                    Our platform uses a wallet-based payment system. Users must maintain sufficient 
                    balance to post advertisements. Wallet top-ups are processed through authorized 
                    agents or support channels.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">5.2 Pricing</h3>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>1 Time Slot: ₹400 per city</li>
                    <li>2 Time Slots: ₹700 per city</li>
                    <li>3-4 Time Slots (24 hours): ₹1,200 per city</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">5.3 Refunds</h3>
                  <p className="text-gray-300">
                    Wallet credits are non-refundable once added. However, if your ad is rejected 
                    for policy violations, the credits will be returned to your wallet. We do not 
                    provide cash refunds.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">6. Free Ads</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">
                  New users receive 2 free ad posts as a trial. Free ads have the following limitations:
                </p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li>Images are blurred in listing view</li>
                  <li>Contact information is hidden in listing view</li>
                  <li>Full details are visible only on the ad detail page</li>
                  <li>Lower priority in search results compared to paid ads</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">7. User Conduct</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">Users agree NOT to:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li>Violate any laws or regulations</li>
                  <li>Harass, threaten, or abuse other users</li>
                  <li>Create multiple accounts to circumvent restrictions</li>
                  <li>Attempt to manipulate our ranking or algorithm</li>
                  <li>Scrape or collect data from our platform</li>
                  <li>Introduce malware or malicious code</li>
                  <li>Interfere with platform operations</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">8. Intellectual Property</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  The Platform and its original content, features, and functionality are owned by 
                  uplino.in and are protected by international copyright, trademark, and other 
                  intellectual property laws. Users retain ownership of content they post but grant 
                  us a license to display and distribute it on our platform.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">9. Disclaimer of Warranties</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE 
                  THE ACCURACY, COMPLETENESS, OR RELIABILITY OF ANY CONTENT. WE ARE NOT RESPONSIBLE 
                  FOR ANY TRANSACTIONS OR INTERACTIONS BETWEEN USERS. USE THE PLATFORM AT YOUR OWN RISK.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">10. Limitation of Liability</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  To the maximum extent permitted by law, uplino.in shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages, including loss 
                  of profits, data, or other intangible losses resulting from your use of the platform.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">11. Account Termination</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  We reserve the right to suspend or terminate your account at our sole discretion, 
                  without notice, for any violation of these terms or for any other reason. Upon 
                  termination, your right to use the platform ceases immediately. Any remaining 
                  wallet balance may be forfeited.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">12. Governing Law</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  These Terms shall be governed by and construed in accordance with the laws of India. 
                  Any disputes arising from these terms shall be subject to the exclusive jurisdiction 
                  of the courts in India.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">13. Changes to Terms</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  We reserve the right to modify these terms at any time. Changes will be effective 
                  immediately upon posting. Your continued use of the platform after changes 
                  constitutes acceptance of the new terms.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">14. Contact Information</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  For questions about these Terms of Service, please contact us at:
                </p>
                <ul className="mt-3 text-gray-400 space-y-1">
                  <li>Email: legal@uplino.in</li>
                  <li>Support: support@uplino.in</li>
                </ul>
              </div>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Terms;
